import { useCallback, useEffect, useRef, useState } from "react";
import { memoryRequest } from "api/assistantMemory";
import { sendAssistantChatMessageStream } from "api";
import { ASSISTANT_NAME } from "utils/assistantIdentity";
import useActionsStore from "stores/useActionsStore";

const MAX_HISTORY_MESSAGES = 8;
// The live action listener normally shows agent changes within a second.
// The refresh after this delay covers a listener that stopped silently.
const ACTION_REFRESH_DELAY_MS = 3000;
const ASSISTANT_ERROR_MESSAGE = `I could not reach ${ASSISTANT_NAME} right now. Please try again.`;
const EMAIL_DRAFT_READY_MESSAGE =
  "I've prepared the email draft for you. Review and send it in the widget.";

const createMessageId = (role, nextId) => `${role}-${Date.now()}-${nextId}`;

const toHistorySources = (sources) => {
  if (!Array.isArray(sources)) return [];

  return sources
    .filter((source) => source?.meetingId)
    .slice(0, 6)
    .map((source) => ({
      meetingId: source.meetingId,
      meetingTitle: source.meetingTitle || "Untitled meeting",
      meetingDateIso: source.meetingDateIso || null,
      docType: source.docType || null,
    }));
};

const toChatHistory = (messages) =>
  messages
    .filter(
      (message) =>
        !message.error &&
        (message.role === "user" || message.role === "assistant") &&
        typeof message.content === "string" &&
        message.content.trim(),
    )
    .slice(-MAX_HISTORY_MESSAGES)
    .map((message) => ({
      role: message.role,
      content: message.content,
      // Lets the server put back what its tools returned in that turn.
      turnId: message.role === "assistant" ? message.turnId : undefined,
      sources:
        message.role === "assistant"
          ? toHistorySources(message.sources)
          : undefined,
    }));

const normalizeCreatedActions = (createdActions) => {
  if (!Array.isArray(createdActions)) return [];

  return createdActions
    .filter((action) => action?.text)
    .map((action) => ({
      type: action.type || "created",
      text: action.text,
      dueDateIso: action.dueDateIso || null,
      priority: action.priority || "medium",
      private: Boolean(action.private),
      completed:
        typeof action.completed === "boolean" ? action.completed : undefined,
      assignee: action.assignee || null,
      updatedFields: Array.isArray(action.updatedFields)
        ? action.updatedFields
        : undefined,
      labels: Array.isArray(action.labels) ? action.labels : [],
      actionTexts: Array.isArray(action.actionTexts) ? action.actionTexts : [],
      createdFromMessage: action.createdFromMessage || null,
    }));
};

const normalizeCalendarEventDrafts = (drafts) => {
  if (!Array.isArray(drafts)) return [];

  return drafts
    .filter((draft) => draft?.startDateTimeIso && draft?.endDateTimeIso)
    .map((draft) => ({
      id: draft.id || `calendar-draft-${draft.startDateTimeIso}`,
      title: draft.title || "Untitled meeting",
      description:
        typeof draft.description === "string" ? draft.description : "",
      startDateTimeIso: draft.startDateTimeIso,
      endDateTimeIso: draft.endDateTimeIso,
      timezone: draft.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      attendees: Array.isArray(draft.attendees) ? draft.attendees : [],
      unresolvedAttendees: Array.isArray(draft.unresolvedAttendees)
        ? draft.unresolvedAttendees
        : [],
      ambiguousAttendees: Array.isArray(draft.ambiguousAttendees)
        ? draft.ambiguousAttendees
        : [],
      conferenceProvider: draft.conferenceProvider || "google_meet",
      requestedSlotAvailable: Boolean(draft.requestedSlotAvailable),
      conflicts: Array.isArray(draft.conflicts) ? draft.conflicts : [],
      suggestedSlots: Array.isArray(draft.suggestedSlots)
        ? draft.suggestedSlots
        : [],
      availabilityWarnings: Array.isArray(draft.availabilityWarnings)
        ? draft.availabilityWarnings
        : [],
      createdFromMessage: draft.createdFromMessage || null,
    }));
};

const normalizeCalendarEventDeletions = (deletions) => {
  if (!Array.isArray(deletions)) return [];

  return deletions
    .filter((deletion) => deletion?.eventId)
    .map((deletion) => ({
      id: deletion.id || `calendar-delete-${deletion.eventId}`,
      eventId: deletion.eventId,
      title: deletion.title || "Untitled meeting",
      startDateTimeIso: deletion.startDateTimeIso || null,
      endDateTimeIso: deletion.endDateTimeIso || null,
      timezone:
        deletion.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      attendees: Array.isArray(deletion.attendees) ? deletion.attendees : [],
      callLink: deletion.callLink || null,
      conferenceProvider: deletion.conferenceProvider || null,
    }));
};

const normalizeEventAttendees = (attendees) =>
  Array.isArray(attendees)
    ? attendees
        .filter((attendee) => attendee?.email)
        .map((attendee) => ({
          email: attendee.email,
          displayName: attendee.displayName || null,
        }))
    : [];

const normalizeEventSide = (side) => {
  const safe = side || {};
  return {
    title: safe.title || "Untitled meeting",
    description: typeof safe.description === "string" ? safe.description : "",
    startDateTimeIso: safe.startDateTimeIso || null,
    endDateTimeIso: safe.endDateTimeIso || null,
    attendees: normalizeEventAttendees(safe.attendees),
  };
};

const normalizeCalendarEventUpdates = (updates) => {
  if (!Array.isArray(updates)) return [];

  return updates
    .filter((update) => update?.eventId)
    .map((update) => {
      const changes = update.changes || {};
      return {
        id: update.id || `calendar-update-${update.eventId}`,
        eventId: update.eventId,
        timezone:
          update.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        current: normalizeEventSide(update.current),
        proposed: normalizeEventSide(update.proposed),
        changes: {
          title: Boolean(changes.title),
          description: Boolean(changes.description),
          time: Boolean(changes.time),
          addedAttendees: normalizeEventAttendees(changes.addedAttendees),
          removedAttendees: normalizeEventAttendees(changes.removedAttendees),
        },
        unresolvedAttendees: Array.isArray(update.unresolvedAttendees)
          ? update.unresolvedAttendees
          : [],
        ambiguousAttendees: Array.isArray(update.ambiguousAttendees)
          ? update.ambiguousAttendees
          : [],
        unresolvedRemoveAttendees: Array.isArray(update.unresolvedRemoveAttendees)
          ? update.unresolvedRemoveAttendees
          : [],
        requestedSlotAvailable: Boolean(update.requestedSlotAvailable),
        conflicts: Array.isArray(update.conflicts) ? update.conflicts : [],
        suggestedSlots: Array.isArray(update.suggestedSlots)
          ? update.suggestedSlots
          : [],
        availabilityWarnings: Array.isArray(update.availabilityWarnings)
          ? update.availabilityWarnings
          : [],
      };
    });
};

const normalizeEmailRecipients = (recipients) => {
  if (!Array.isArray(recipients)) return [];

  return recipients
    .map((recipient) => ({
      email:
        typeof recipient?.email === "string"
          ? recipient.email.trim().toLowerCase()
          : "",
      displayName: recipient?.displayName || recipient?.email || "",
    }))
    .filter((recipient) => recipient.email);
};

const normalizeEmailDrafts = (drafts) => {
  if (!Array.isArray(drafts)) return [];

  return drafts
    .filter((draft) => draft?.subject && draft?.body)
    .map((draft) => ({
      id: draft.id || `email-draft-${draft.subject}`,
      toRecipients: normalizeEmailRecipients(draft.toRecipients),
      ccRecipients: normalizeEmailRecipients(draft.ccRecipients),
      subject: draft.subject,
      body: draft.body,
      senderEmail: draft.senderEmail || null,
      createdFromMessage: draft.createdFromMessage || null,
    }))
    .filter(
      (draft) => draft.toRecipients.length > 0 || draft.ccRecipients.length > 0,
    );
};

const useAssistantChat = () => {
  const [messages, setMessages] = useState([]);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [assistantProgress, setAssistantProgress] = useState(null);
  const messageIdRef = useRef(0);
  const activeRequestRef = useRef(null);
  const refreshActions = useActionsStore((state) => state.refreshActions);

  useEffect(() => {
    const pending = messages.flatMap(message => (message.memoryReceipts || [])
      .filter(receipt => receipt.sourceId && (receipt.kind === "submission" ? ["pending", "processing"] : ["processing", "partial"]).includes(receipt.status))
      .map(receipt => ({ messageId: message.id, sourceId: receipt.sourceId, kind: receipt.kind })));
    if (!pending.length) return;
    const controller = new AbortController();
    let running = false;
    const poll = async () => {
      if (running || document.visibilityState === "hidden") return;
      running = true;
      try {
        const updates = await Promise.all(pending.map(async item => ({ ...item,
          receipt: await memoryRequest((item.kind === "submission" ? "/memory-submissions/" : "/memories/requests/") + encodeURIComponent(item.sourceId), { signal: controller.signal }),
        })));
        if (controller.signal.aborted) return;
        setMessages(current => current.map(message => {
          const update = updates.find(item => item.messageId === message.id);
          if (!update) return message;
          return { ...message, content: update.receipt.reply,
            memoryReceipts: message.memoryReceipts.map(receipt => receipt.sourceId === update.sourceId
              ? { ...receipt, ...update.receipt } : receipt) };
        }));
      } catch {
        // A status-read outage is not evidence that saving failed.
      } finally { running = false; }
    };
    const timer = setInterval(poll, 5000);
    return () => { clearInterval(timer); controller.abort(); };
  }, [messages]);

  const nextMessageId = useCallback((role) => {
    messageIdRef.current += 1;
    return createMessageId(role, messageIdRef.current);
  }, []);

  const sendMessage = useCallback(
    async (content, options = {}) => {
      const { voiceMode = false, onDelta, onAck, onProgress, signal } = options;
      const trimmedContent = typeof content === "string" ? content.trim() : "";
      const activeRequest = activeRequestRef.current;
      if (
        !trimmedContent ||
        signal?.aborted ||
        (activeRequest && !activeRequest.signal?.aborted)
      ) {
        return null;
      }

      const turnId = options.turnId || crypto.randomUUID();
      const request = { signal, turnId };
      activeRequestRef.current = request;

      const userMessage = {
        id: nextMessageId("user"),
        turnId,
        role: "user",
        content: trimmedContent,
      };

      const chatHistory = toChatHistory(messages);

      setMessages((currentMessages) => [...currentMessages, userMessage]);
      setIsAiThinking(true);
      setAssistantProgress(null);

      try {
        const response = await sendAssistantChatMessageStream({
          message: trimmedContent,
          turnId,
          chatHistory,
          voiceMode,
          onDelta,
          onAck,
          signal,
          onProgress: (progress) => {
            if (!progress?.label) return;
            setAssistantProgress(progress);
            onProgress?.(progress);
          },
        });

        const emailDrafts = normalizeEmailDrafts(response.emailDrafts);

        const assistantMessage = {
          id: nextMessageId("assistant"),
          turnId,
          role: "assistant",
          // Voice turns already carry the spoken email-draft reply from the
          // backend; replacing it here would desync transcript and audio.
          content:
            emailDrafts.length && !voiceMode
              ? EMAIL_DRAFT_READY_MESSAGE
              : response.assistantReply,
          createdActions: normalizeCreatedActions(
            response.actionReceipts?.length
              ? response.actionReceipts
              : response.createdActions,
          ),
          calendarEventDrafts: normalizeCalendarEventDrafts(
            response.calendarEventDrafts,
          ),
          calendarEventDeletions: normalizeCalendarEventDeletions(
            response.calendarEventDeletions,
          ),
          calendarEventUpdates: normalizeCalendarEventUpdates(
            response.calendarEventUpdates,
          ),
          emailDrafts,
          sources: response.sources,
          memorySources: response.memorySources || [],
          memoryReceipts: response.memoryReceipts || [],
          meta: response.meta,
        };

        setMessages((currentMessages) => [
          ...currentMessages,
          assistantMessage,
        ]);
        if (response.actionReceipts?.length) {
          setTimeout(refreshActions, ACTION_REFRESH_DELAY_MS);
        }
        return assistantMessage;
      } catch (error) {
        // A voice barge-in aborts the fetch; that's a cancel, not a failure.
        if (error?.name === "AbortError" || signal?.aborted) {
          return null;
        }
        console.error("assistant chat request failed:", error);
        setMessages((currentMessages) => [
          ...currentMessages,
          {
            id: nextMessageId("assistant"),
            role: "assistant",
            content: error?.message?.trim() || ASSISTANT_ERROR_MESSAGE,
            error: true,
          },
        ]);
        return null;
      } finally {
        if (activeRequestRef.current === request) {
          activeRequestRef.current = null;
          setIsAiThinking(false);
          setAssistantProgress(null);
        }
      }
    },
    [messages, nextMessageId, refreshActions],
  );

  return {
    messages,
    isAiThinking,
    assistantProgress,
    sendMessage,
  };
};

export default useAssistantChat;
